// Modules:
import React, { useState, useRef, useEffect, useContext } from 'react';

// Material UI:
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';

// Chart.js:
import { Bar } from 'react-chartjs-2';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';
import {
    formatNumber,
    generateAnnotations,
    processCounties,
    handleMissingSuppressed,
} from 'utilities/utils';

// Components:
import Selector from './Selector';

const BarLineMix = ({
    counties,
    json,
    sortByAxis,
    selection,
    handleDropdownSelect,
    dataUnavailable,
    setDataUnavailable,
    checkIfNull,
    styles,
    setMissingSuppressedModalOpen,
}) => {
    // // State // //
    const [chartData, setChartData] = useState({});

    // // Context // //
    const { tab: currentTab, topic: currentTopic } = useContext(
        TopicConfigContext
    );

    // // Refs // //
    const chartRef = useRef();

    // // Effects: // //
    useEffect(() => {
        const loadChart = () => {
            // Reset defaults if chart loads for new data:
            setChartData({});
            setDataUnavailable(false);
            if (!json || !selection) {
                return;
            }

            const { chartConfigs } = currentTab.chartSettings;

            const getChartData = () => {
                const data = {}; // The object chart.js will be passed
                const xAxisField = currentTab.chartSettings.chartXAxisField; // x-axis labels are typically the time intervals for the graph
                const dataSetNames = currentTab.chartSettings.chartDataSets;

                // Sort labels by year:
                const sortedData = sortByAxis(json, xAxisField);
                // Extract labels for chart from data:
                data.labels = sortedData.map((item) => item[xAxisField]);

                const datasetArray = [];

                dataSetNames.forEach((setName) => {
                    const dataset = chartConfigs.find(
                        (x) => x.setName === setName
                    );
                    dataset.data = json.map((x) =>
                        handleMissingSuppressed(x[setName])
                    );
                    datasetArray.push(dataset);
                });

                // If all values are null, the following method will change `dataUnavailable` to true:
                const allNull = checkIfNull(datasetArray);
                if (allNull) {
                    setDataUnavailable(true);
                }

                // if data available, set color palette and put chart data into state
                datasetArray.forEach((dataset) => {
                    if (dataset.type === 'line') {
                        dataset.backgroundColor = 'transparent';
                        dataset.borderColor = dataset.color;
                    } else {
                        dataset.backgroundColor = dataset.color;
                        dataset.borderColor = dataset.color;
                    }
                });

                data.datasets = datasetArray;

                setChartData(data);
            };

            getChartData();
        };
        loadChart();
    }, [
        selection,
        json,
        checkIfNull,
        sortByAxis,
        currentTab,
        setDataUnavailable,
    ]);

    // Only Birth Outcomes has nationwide data, removing it as a selectable option for all other topics.
    const processedCounties = processCounties(
        counties,
        currentTopic.topicTitle
    );

    return (
        <>
            <Grid container justify="center">
                {processedCounties.length && (
                    <Selector
                        currentTab={currentTab}
                        processedCounties={processedCounties}
                        handleDropdownSelect={handleDropdownSelect}
                        selection={selection}
                        styles={styles}
                    ></Selector>
                )}
            </Grid>
            {dataUnavailable ? (
                <Grid item xs={12}>
                    <div className="data-unavailable">
                        <Alert
                            sx={styles}
                            severity="info"
                            onClick={() => setMissingSuppressedModalOpen(true)}
                        >
                            Data are not available to display for{' '}
                            {counties && counties.length
                                ? counties.find(
                                      (county) => county.code === selection
                                  ).county
                                : 'selection.'}
                            .
                        </Alert>
                    </div>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <div className="chart-container">
                        {Object.keys(chartData).length ? (
                            <Bar
                                ref={chartRef}
                                data={chartData}
                                width={100}
                                height={50}
                                options={{
                                    plugins: {
                                        title: {
                                            display:
                                                currentTab.chartSettings
                                                    .displayChartTitle,
                                            text:
                                                currentTab.chartSettings
                                                    .chartTitle,
                                        },
                                        subtitle: {
                                            display:
                                                currentTab.chartSettings
                                                    .displayChartSubtitle ||
                                                false, // TODO:  The OR statements for these two keys are to account for a real subtitle only being in place for one topic.  Once the subtitle is given for all charts, this can be removed.
                                            text:
                                                currentTab.chartSettings
                                                    .chartSubtitle || '',
                                        },
                                        annotation: {
                                            // Mixed charts are not stratified, so no stratification is passed.
                                            // A chart configured with 'discontinuityRequiresStratification' will not show the graphic here.
                                            annotations: currentTab
                                                .chartSettings
                                                .displayChartDiscontinuityGraphic
                                                ? generateAnnotations(
                                                      chartData,
                                                      currentTab.chartSettings
                                                  )
                                                : null,
                                        },
                                        legend: {
                                            position: 'bottom',
                                        },
                                        tooltip: {
                                            enabled: true,
                                            mode: 'index',
                                            intersect: false,
                                            callbacks: {
                                                labelColor: (context) => ({
                                                    borderColor:
                                                        context.dataset
                                                            .borderColor,
                                                    backgroundColor:
                                                        context.dataset
                                                            .borderColor,
                                                }),
                                                label: ({
                                                    dataIndex,
                                                    dataset,
                                                }) => {
                                                    // This solution is to differentiate between rate and non-rate data sets, as rate data sets need a trailing .0 for whole values (9 => 9.0).
                                                    // This is reliant on the 'setName' key including the word 'rate' in some capacity, but may need to be reworked to have a more consistent condition.
                                                    const isRate = dataset.setName.match(
                                                        /rate/gi
                                                    );

                                                    const value =
                                                        dataset.data[dataIndex];

                                                    return isRate
                                                        ? formatNumber(value)
                                                        : value.toLocaleString();
                                                },
                                            },
                                        },
                                    },

                                    maintainAspectRatio: false,

                                    scales: {
                                        [currentTab.chartSettings
                                            .yAxisIdLeft]: {
                                            type: 'linear',
                                            grace: '20%',
                                            beginAtZero: true,
                                            ticks: {
                                                userCallback: (value) =>
                                                    value &&
                                                    value.toLocaleString(),
                                            },
                                            position: 'left',
                                            display: true,
                                            title: {
                                                display:
                                                    currentTab.chartSettings
                                                        .displayYAxisLabel,
                                                text:
                                                    currentTab.chartSettings
                                                        .yAxisLabelLeft,
                                            },
                                        },
                                        [currentTab.chartSettings
                                            .yAxisIdRight]: {
                                            type: 'linear',
                                            grace: '20%',
                                            beginAtZero: true,
                                            ticks: {
                                                userCallback: (value) =>
                                                    value &&
                                                    value.toLocaleString(),
                                            },
                                            position: 'right',
                                            title: {
                                                display:
                                                    currentTab.chartSettings
                                                        .displayYAxisLabel,
                                                text:
                                                    currentTab.chartSettings
                                                        .yAxisLabelRight,
                                            },
                                        },
                                        x: {
                                            title: {
                                                display:
                                                    currentTab.chartSettings
                                                        .displayXAxisLabel,
                                                text:
                                                    currentTab.chartSettings
                                                        .xAxisLabel,
                                            },
                                        },
                                    },
                                }}
                            />
                        ) : null}
                    </div>
                </Grid>
            )}
        </>
    );
};

export default BarLineMix;
